import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactLoading from "react-loading";
import { motion } from "framer-motion";
import Breadcrumb from "../../../components/breadcrumb";
import Footer from "../../../components/footer";
import PageBackgroundPattern from "../../../components/PageBackgroundPattern";
import PartidoLeadersPanel from "../components/PartidoLeadersPanel";
import PartidoPlayerModal from "../components/PartidoPlayerModal";
import PartidoPlayByPlaySection from "../components/PartidoPlayByPlaySection";
import PartidoScoreEvolutionChart from "../components/PartidoScoreEvolutionChart";
import PartidoShotChartSection from "../components/PartidoShotChartSection";
import PartidoStatsTable from "../components/PartidoStatsTable";
import PartidoSummaryCard from "../components/PartidoSummaryCard";
import PartidoTrendsPanel from "../components/PartidoTrendsPanel";
import usePartidoData from "../hooks/usePartidoData";

const VIEW_OPTIONS = [
    { id: "estadisticas", label: "Estadísticas" },
    { id: "comparacion", label: "Comparación" },
    { id: "lideres", label: "Líderes" },
    { id: "jugada_a_jugada", label: "Jugada a jugada" },
    { id: "evolucion", label: "Evolución del marcador" },
    { id: "carta_tiro", label: "Carta de tiro" },
];

function Partido() {
    const { partidoId } = useParams();
    const [activeView, setActiveView] = useState("estadisticas");
    const [selectedPlayer, setSelectedPlayer] = useState("");
    const { isLoading, isError, viewModel } = usePartidoData(partidoId);
    // Cache de perfiles de jugador consultados en este partido, evita refetch al reabrir el modal
    const playerProfileCacheRef = useRef(new Map());
    const aguadaTeam = viewModel?.home?.isAguada ? viewModel.home : viewModel?.away;
    const rivalTeam = viewModel?.home?.isAguada ? viewModel?.away : viewModel?.home;
    const leadersAccent = aguadaTeam?.isWinner ? "green" : "red";
    const rivalName = rivalTeam?.name || "Rival";
    const aguadaCode = (aguadaTeam?.name || "Aguada").replace(/\s+/g, "").substring(0, 3).toUpperCase();
    const rivalCode = (rivalTeam?.name || "Rival").replace(/\s+/g, "").substring(0, 3).toUpperCase();

    useEffect(() => {
        if (!viewModel?.liga) {
            document.title = "Partido";
            return;
        }

        document.title = `${viewModel.liga} | ${viewModel.resultado || "Partido"}`;
    }, [viewModel]);

    const handleOpenPlayerModal = (playerName) => {
        if (!playerName) return;
        setSelectedPlayer(playerName);
    };

    const handleClosePlayerModal = () => {
        setSelectedPlayer("");
    };

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#062a1a] pt-16">
            <PageBackgroundPattern />
            <main className="relative z-10 max-w-7xl mx-auto w-full px-4 md:px-10 pt-2 pb-8 md:pt-2 md:pb-10">
                <Breadcrumb
                    items={[
                        { label: "Inicio", to: "/" },
                        { label: "Partidos", to: "/partidos/buscarpartido" },
                        ...(viewModel?.liga ? [{ label: viewModel.liga, to: `/partidos/buscarpartido?liga=${encodeURIComponent(viewModel.liga)}`, isCurrent: false }] : []),
                        {
                            label: `${viewModel?.home?.name || "Aguada"} vs ${viewModel?.away?.name || "Rival"}`,
                            isCurrent: true,
                        },
                    ]}
                    separator="-"
                    className="mb-4"
                />

                {isLoading ? (
                    <div className="rounded-xl border border-white/10 bg-[#102216] p-10">
                        <ReactLoading type="bars" color="#22c55e" width={80} className="text-center m-auto mb-10" />
                        <p className="text-center text-slate-200 text-lg font-semibold">Buscando estadísticas...</p>
                    </div>
                ) : null}

                {isError && !isLoading ? (
                    <div className="rounded-xl border border-red-400/30 bg-red-950/20 p-8 text-center text-red-200 font-medium mb-8">
                        No se pudo cargar el partido en este momento.
                    </div>
                ) : null}

                {!isLoading && !isError ? (
                    <>
                        <PartidoSummaryCard viewModel={viewModel} />

                        <div className="mb-6 md:mb-8 overflow-x-auto md:overflow-visible md:flex md:justify-center scrollbar-hide">
                            <div className="inline-flex p-1 bg-[#0f1f15] border border-white/10 rounded-xl shadow-2xl gap-0.5 sm:gap-1 min-w-max">
                                {VIEW_OPTIONS.map((option) => (
                                    <button
                                        key={option.id}
                                        type="button"
                                        onClick={() => setActiveView(option.id)}
                                        className={`relative z-10 px-2 sm:px-3 md:px-6 py-2 sm:py-2.5 rounded-lg text-[10px] sm:text-xs md:text-sm font-bold transition-colors duration-200 whitespace-nowrap ${activeView === option.id
                                            ? "text-[#062a1a]"
                                            : "text-slate-400 hover:text-white"
                                            }`}
                                        title={option.label}
                                    >
                                        {activeView === option.id ? (
                                            <motion.span
                                                layoutId="partido-view-toggle-pill"
                                                transition={{ type: "spring", stiffness: 420, damping: 34 }}
                                                className="absolute inset-0 -z-10 rounded-lg bg-green-500"
                                            />
                                        ) : null}
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {!viewModel.hasStats ? (
                            <div className="rounded-xl border border-white/10 bg-[#102216] p-8 text-center text-slate-300 font-medium mb-8">
                                {viewModel.statusMessage || "Estadísticas no recopiladas"}
                            </div>
                        ) : null}

                        {activeView === "estadisticas" && viewModel.hasStats ? (
                            <div className="grid grid-cols-1 gap-8 mb-10">
                                <PartidoStatsTable
                                    title={viewModel.home.name}
                                    players={viewModel.home.players}
                                    totals={viewModel.home.totals}
                                    isAguadaTeam={viewModel.home.isAguada}
                                    accent={viewModel.home.isWinner ? "green" : "red"}
                                    minutes={viewModel.minutes.home}
                                    coach={viewModel.home.coach}
                                    onPlayerClick={viewModel.home.isAguada ? handleOpenPlayerModal : undefined}
                                />

                                <PartidoStatsTable
                                    title={viewModel.away.name}
                                    players={viewModel.away.players}
                                    totals={viewModel.away.totals}
                                    isAguadaTeam={viewModel.away.isAguada}
                                    accent={viewModel.away.isWinner ? "green" : "red"}
                                    minutes={viewModel.minutes.away}
                                    coach={viewModel.away.coach}
                                    onPlayerClick={viewModel.away.isAguada ? handleOpenPlayerModal : undefined}
                                />
                            </div>
                        ) : null}

                        {activeView === "evolucion" && viewModel.hasStats ? (
                            <div className="mb-8">
                                <PartidoScoreEvolutionChart
                                    scoreEvolution={viewModel.scoreEvolution}
                                    localName={viewModel.home?.name}
                                    awayName={viewModel.away?.name}
                                    isHomeAguada={viewModel.home?.isAguada}
                                    aguadaWon={aguadaTeam?.isWinner}
                                />

                                {viewModel.pbpTrends?.canCalculate ? (
                                    <div className="mt-4 sm:mt-6">
                                        <PartidoTrendsPanel
                                            trends={viewModel.pbpTrends}
                                            trendsComparison={viewModel.trendsComparison}
                                            aguadaTotals={aguadaTeam?.totals}
                                            rivalTotals={rivalTeam?.totals}
                                            aguadaName={aguadaTeam?.name || "Aguada"}
                                            rivalName={rivalTeam?.name || "Rival"}
                                            aguadaCode={aguadaCode}
                                            rivalCode={rivalCode}
                                            aguadaWon={aguadaTeam?.isWinner}
                                            showOnlyTrends
                                        />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {activeView === "comparacion" && viewModel.hasStats ? (
                            <div className="mb-8">
                                <PartidoTrendsPanel
                                    trends={viewModel.pbpTrends}
                                    trendsComparison={viewModel.trendsComparison}
                                    aguadaTotals={aguadaTeam?.totals}
                                    rivalTotals={rivalTeam?.totals}
                                    aguadaName={aguadaTeam?.name || "Aguada"}
                                    rivalName={rivalTeam?.name || "Rival"}
                                    aguadaCode={aguadaCode}
                                    rivalCode={rivalCode}
                                    aguadaWon={aguadaTeam?.isWinner}
                                    isHomeAguada={viewModel.home?.isAguada}
                                />
                            </div>
                        ) : null}

                        {activeView === "jugada_a_jugada" && viewModel.hasStats ? (
                            <div className="mb-8">
                                <PartidoPlayByPlaySection playByPlay={viewModel.playByPlay} />
                            </div>
                        ) : null}

                        {activeView === "carta_tiro" && viewModel.hasStats ? (
                            <div className="mb-8">
                                <PartidoShotChartSection
                                    shotChart={viewModel.shotChart}
                                    rivalLabel={(rivalName || "Rival").toUpperCase()}
                                    aguadaWon={aguadaTeam?.isWinner}
                                />
                            </div>
                        ) : null}

                        {activeView === "lideres" && viewModel.hasStats ? (
                            <div className="mb-8">
                                <PartidoLeadersPanel 
                                    leaders={viewModel.leaders} 
                                    rivalsLeaders={viewModel.rivalsLeaders}
                                    rivalName={rivalName}
                                    accent={leadersAccent}
                                    rivalAccent={aguadaTeam?.isWinner ? "red" : "green"}
                                />
                            </div>
                        ) : null}

                    </>
                ) : null}
            </main>

            <PartidoPlayerModal
                playerName={selectedPlayer}
                leagueName={viewModel?.liga}
                isOpen={Boolean(selectedPlayer)}
                onClose={handleClosePlayerModal}
                cacheRef={playerProfileCacheRef}
            />

            <div className="relative z-10">
                <Footer />
            </div>
        </div>
    );
}

export default Partido;
